import classNames from 'classnames';
import EngagementChart from 'Components/company/components/EngagementChart';
import ChartSpinner from 'Components/sri/ChartSpinner';
import { useSelector } from 'react-redux';
import { selectCompanyProfile } from 'Selector/CompaniesSelector';
import styled from 'styled-components';
import { SeriesType, WorkbenchStatsResponseType } from 'Types/PersonTypes';
import { IsTablet, IsMobile } from 'Utils/UtilityFunctions';
import {
  boxShadow, maxWidth, minWidthApp, retina,
  secondaryFont, textTruncateSnippet,
  maxMobile, maxTablet,
} from 'Utils/utilsStyle';
import ShareIcon from '../assets/svgIcons/ShareIcon';
import SkeletonCustom from '../skeleton/SkeletonCustom';
import EntityTile from './EntityTile';
import GlobalButton from './GlobalButton';

const Wrapper = styled.div`
  &.listViewWorkbenchStats {
    border-radius: 8px;
    /* height: 100%; */
    .activity_wrapper { 
      width: 100%;
      .data-entity { 
        width: 100%;
      }
    }
    .listview-wrapper{
      width: 100%;
      display: flex;
      flex-direction: column;

      .activity_wrapper {
        width: 100%;
        .data-entity{
          width: 100%;

          &:not(:last-child){
            margin-right: 10px;
          }
        }
      }
      .analyze_button {
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        .analyzeACC-button {
          @media all and (max-width: ${maxWidth}) {
            ${textTruncateSnippet};
          }
          padding: 4px 18px;
          @media all and (max-width: ${maxWidth}) ,${retina} {
            height: 40px;
          }
        }
      }
    }
  }
  .activity_wrapper{
    display: flex;
    justify-content: space-between;
    gap: 16px;
    @media all and (max-width: ${maxTablet}) {
      flex-wrap: wrap;
      gap: 16px;
    }
  }
  .shareIcon-svg{
    width: 35px;
    height: 33px;
    transform: rotate(90deg);
    margin-right: 10px;
  }
  .analyzeACC-button{
    width: 100%;
    font-size: 21px;
    line-height: 26px;
    margin: 0;
  }
  .analyze_button {
    .analyzeACC-button {
      @media all and (max-width: ${minWidthApp}){
        font-size: 14px;
      }
      @media all and (max-width: ${maxTablet}){ 
        padding: 7px;
        height: 32px;
        font-size: 12px;
        display: flex;
        align-items: center;
      }
      @media all and (max-width: ${maxMobile}){
        font-size: 10px;
      }
    }
    .shareIcon-svg {
      @media all and (max-width: ${maxTablet}){ 
        width: 12px;
        margin-bottom: 0px;
        margin-right: 10px;
      }
    }
  }
  .data-entity{
   @media all and (max-width: ${maxTablet}) {
    max-width: calc(50% - 8px);
    min-width: calc(50% - 8px);
    flex: 1;
   }
  }
`;

const Overlay = styled.div`
  max-height: 317px;
  box-shadow: ${boxShadow};
  border-radius: 10px;
  background: #fff;
  padding: 24px 42px;
  margin-bottom: 15px;
  margin-top: 15px;
  @media all and (max-width: ${maxTablet}){
   padding: 16px;
  }
  @media all and (max-width: ${maxMobile}){
   padding: 12px;
  }
  .title{
    font-size: 25px;
    font-weight: 700;
    letter-spacing: -0.7px;
    font-family: ${secondaryFont};
    color: #424B50;
    @media all and (max-width: ${maxTablet}){
     font-size: 18px;
    }
  }
  .chart-overlay-loader {
    height: 200px;
  }
`;

type Props = {
  stats: WorkbenchStatsResponseType[],
  loading?: boolean,
  listview?: boolean,
  isSignleArticle?: boolean,
} & typeof defaultProps;

const defaultProps = {
  loading: false,
  listview: false,
  isSignleArticle: false,
};

const dateUTC = (param: Date) => Date.UTC(Number(param.toString().split('-')[0]), Number(param.toString().split('-')[1]) - 1, Number(param.toString().split('-')[2]));

const WorkbenchStat = function WorkbenchStat(props: Props) {
  const {
    stats, loading, listview, isSignleArticle,
  } = props;
  const statTiles = stats?.filter((stat) => stat.type !== 'engagement' && stat.type !== 'contactId');
  const engagementGraph = stats?.find((stat) => stat.type === 'engagement');
  const contactId = stats?.find((stat) => stat.type === 'contactId') || undefined;
  const isTab = IsTablet();
  const isMobile = IsMobile();
  const companyProfile = useSelector(selectCompanyProfile);
  const url = process.env.REACT_APP_WORKBENCH_URL || '';

  const xAxis: number[] = engagementGraph?.data?.avg_clicks
    ?.map((obj) => (new Date(obj.date)).getMonth()) || [];

  const getData = function getData(type: string) {
    let data;
    if (type === 'totalOpens') {
      data = engagementGraph?.data?.total_opens
        ?.map((open) => [dateUTC(open.date), open.score]) || [];
    }
    if (type === 'totalClicks') {
      data = engagementGraph?.data?.total_clicks
        ?.map((click) => [dateUTC(click.date), click.score]) || [];
    }
    if (type === 'totalOpensUnique') {
      data = engagementGraph?.data?.avg_opens
        ?.map((avgOpen) => [dateUTC(avgOpen.date), avgOpen.score]) || [];
    }
    if (type === 'totalClickUnique') {
      data = engagementGraph?.data?.avg_clicks
        ?.map((avgClick) => [dateUTC(avgClick.date), avgClick.score]) || [];
    }
    return data;
  };

  const series: SeriesType[] = statTiles?.map((item) => ({
    name: item.title,
    color: item.color,
    type: 'spline',
    data: getData(item.type),
  })) || [];

  const handleClick = () => {
    if (!loading && contactId) {
      window.open(`${url}analytics/contacts/dashboard/?contact_id=${contactId.contactId || 0}`, '_blank');
    } else if (!loading && companyProfile) {
      window.open(`${url}analytics/accounts/dashboard/?account_id=${companyProfile.companyId || 0}&allCampaigns=true`, '_blank');
    }
  };

  return (
    <Wrapper className={classNames({
      listViewWorkbenchStats: listview && !isTab,
    })}
    >
      <div className="listview-wrapper">
        <div className="activity_wrapper">
          {loading ? [1, 2, 3, 4].map(() => (
            <span className="resolution-fix workbenchStats-company">
              <SkeletonCustom width="130px" height="118px" margin="5px 0" />
            </span>
          ))
            : statTiles.map((stat) => (
              <EntityTile
                topLeftText={stat.title}
                company={stat.value}
                percentage={stat.percentage}
                engagements={stat.description}
                bgColor={stat.color}
                type="stats"
              />
            ))}
        </div>
        {!listview && (
          <Overlay className="statsChartWrapper">
            <div className="head-wrap d-flex justify-content-between">
              {loading ? <SkeletonCustom width="270px" height="40px" />
                : <h6 className="title mb-0">{engagementGraph?.title}</h6>}
            </div>
            {loading ? <ChartSpinner />
              : <EngagementChart axis={xAxis || []} data={series || [{}]} />}
          </Overlay>
        )}
        <div className={classNames('analyze_button', { mbc10: listview && isTab })}>
          {loading ? <SkeletonCustom width="100%" height="40px" margin="5px 0" />
            : (
              <GlobalButton
                title={(listview && !isMobile) || (isSignleArticle && !isMobile) ? 'Analyze in xiQ Workbench' : 'Analyze Account Engagement in xiQ Workbench'}
                textColor="#fff"
                bgColor="#1EC252"
                innerSpace="10px 15px"
                heightSm="45px"
                LeftSpace="7px"
                // disabled
                IconLeft={<ShareIcon />}
                buttonClassName="analyzeACC-button"
                onButtonClick={handleClick}
              />
            )}
        </div>
      </div>
    </Wrapper>
  );
};

WorkbenchStat.defaultProps = defaultProps;
export default WorkbenchStat;
